import { strToU8, zipSync } from "fflate";

/**
 * A minimal Excel workbook writer: named sheets of plain rows, nothing else.
 *
 * Written here rather than added as a library because the stats export needs only this, and the
 * spreadsheet libraries that do everything are hundreds of kilobytes. An .xlsx file is a zip of a
 * few XML parts; `fflate` already ships with the admin app (jspdf uses it) to build the zip.
 *
 * Numbers that look like numbers are written as numbers so Excel can sum them; everything else is
 * an inline string. No styles, formulas or shared strings.
 *
 * The parts holding names and values are built as XML documents and written by `XMLSerializer`, so
 * the browser escapes every organization name and cell; nothing here escapes by hand.
 */

export interface Sheet {
    name: string;
    rows: string[][];
}

/** Excel's own limit on a sheet name. */
const MAX_SHEET_NAME = 31;

const NUMBER = /^-?\d+(\.\d+)?$/;

const SPREADSHEET_NS = "http://schemas.openxmlformats.org/spreadsheetml/2006/main";
const RELATIONSHIPS_NS = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
const XML_NS = "http://www.w3.org/XML/1998/namespace";
const XMLNS_NS = "http://www.w3.org/2000/xmlns/";

/**
 * Control characters other than tab and newlines cannot appear in XML at all, escaped or not, and
 * the serializer writes them through, which leaves a file Excel refuses to open. So they go.
 */
const xmlSafe = (value: string): string => value.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, "");

/** A part of the workbook in the spreadsheet namespace, with a helper for its elements. */
const spreadsheetPart = (root: string) => {
    const doc = document.implementation.createDocument(SPREADSHEET_NS, root, null);
    const element = (parent: Element, name: string, attributes: Record<string, string> = {}) => {
        const child = parent.appendChild(doc.createElementNS(SPREADSHEET_NS, name));
        for (const [
            key,
            value,
        ] of Object.entries(attributes)) {
            child.setAttribute(key, xmlSafe(value));
        }
        return child;
    };
    return { doc, root: doc.documentElement, element };
};

const serialize = (doc: XMLDocument): string =>
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>${new XMLSerializer().serializeToString(doc)}`;

/** A1-style column letters: 0 → A, 25 → Z, 26 → AA. */
const columnName = (index: number): string => {
    let name = "";
    for (let n = index + 1; n > 0; n = Math.floor((n - 1) / 26)) {
        name = String.fromCharCode(65 + ((n - 1) % 26)) + name;
    }
    return name;
};

/**
 * Sheet names Excel will accept: no `[ ] : * ? / \`, at most 31 characters, unique ignoring case,
 * and not empty. A clash gets " (2)", " (3)" and so on, trimmed to fit.
 */
export const sheetNames = (names: string[]): string[] => {
    const taken = new Set<string>();
    return names.map((raw) => {
        const base =
            raw
                .replace(/[[\]:*?/\\]/g, " ")
                .replace(/\s+/g, " ")
                .trim()
                .slice(0, MAX_SHEET_NAME)
                .trim() || "Sheet";
        let name = base;
        for (let n = 2; taken.has(name.toLowerCase()); n += 1) {
            const suffix = ` (${n})`;
            name = base.slice(0, MAX_SHEET_NAME - suffix.length) + suffix;
        }
        taken.add(name.toLowerCase());
        return name;
    });
};

const sheetXml = (rows: string[][]): string => {
    const { doc, root, element } = spreadsheetPart("worksheet");
    const sheetData = element(root, "sheetData");
    rows.forEach((row, r) => {
        const rowElement = element(sheetData, "row", { r: String(r + 1) });
        row.forEach((value, c) => {
            const ref = `${columnName(c)}${r + 1}`;
            if (NUMBER.test(value)) {
                element(element(rowElement, "c", { r: ref }), "v").textContent = value;
                return;
            }
            const text = element(element(element(rowElement, "c", { r: ref, t: "inlineStr" }), "is"), "t");
            text.setAttributeNS(XML_NS, "xml:space", "preserve");
            text.textContent = xmlSafe(value);
        });
    });
    return serialize(doc);
};

const workbookXml = (names: string[]): string => {
    const { doc, root, element } = spreadsheetPart("workbook");
    root.setAttributeNS(XMLNS_NS, "xmlns:r", RELATIONSHIPS_NS);
    const sheets = element(root, "sheets");
    names.forEach((name, i) => {
        const sheet = element(sheets, "sheet", { name, sheetId: String(i + 1) });
        sheet.setAttributeNS(RELATIONSHIPS_NS, "r:id", `rId${i + 1}`);
    });
    return serialize(doc);
};

/** The workbook as .xlsx bytes, sheets in the order given. */
export const buildXlsx = (sheets: Sheet[]): Uint8Array => {
    if (sheets.length === 0) throw new Error("A workbook needs at least one sheet");
    const names = sheetNames(sheets.map((sheet) => sheet.name));
    const files: Record<string, Uint8Array> = {
        "[Content_Types].xml": strToU8(
            `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>${sheets
                .map(
                    (_, i) =>
                        `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
                )
                .join("")}</Types>`,
        ),
        "_rels/.rels": strToU8(
            `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`,
        ),
        "xl/workbook.xml": strToU8(workbookXml(names)),
        "xl/_rels/workbook.xml.rels": strToU8(
            `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${sheets
                .map(
                    (_, i) =>
                        `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`,
                )
                .join("")}</Relationships>`,
        ),
    };
    sheets.forEach((sheet, i) => {
        files[`xl/worksheets/sheet${i + 1}.xml`] = strToU8(sheetXml(sheet.rows));
    });
    return zipSync(files);
};

/** Saves the workbook through the browser, as exportCsv does for a CSV. */
export const downloadXlsx = (filename: string, sheets: Sheet[]): void => {
    // fflate allocates an ordinary ArrayBuffer; its types just do not say so.
    const blob = new Blob([buildXlsx(sheets) as Uint8Array<ArrayBuffer>], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
};
